<template>
  <div ref="swiper" class="columns-list swiper-container">
    <div class="swiper-wrapper">
      <div
        v-for="(column, columnIndex) in items"
        :key="`column-${columnIndex}`"
        :class="[
          'swiper-slide',
          'columns-list__item',
          { 'columns-list__item--empty': column.deals_count === 0 },
        ]"
      >
        <TagsItem
          v-bind="{ ...column, count: column.deals_count }"
          :key="`tag-item-${columnIndex}`"
          class="columns-list__tag"
          @click.native="handleTagItemClick(columnIndex)"
        />

        <DealCard
          v-for="(deal, dealIndex) in column.deals"
          :key="`deal-card-${columnIndex}-${dealIndex}`"
          v-bind="{ deal, columnName: column.name }"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Swiper from 'swiper';
import TagsItem from '@/components/dashboard/TagsItem.vue';
import DealCard from '@/components/dashboard/DealCard.vue';
import { GETTER, MUTATION } from '@/store/CONSTANTS';

export default {
  name: 'ColumnsList',
  components: {
    TagsItem,
    DealCard,
  },
  props: {
    items: {
      type: Array,
      required: true,
    },
  },
  data() {
    const isTouchDevice = !!('ontouchstart' in window || navigator.maxTouchPoints);

    return {
      isTouchDevice,
      swiper: null,
    };
  },
  computed: {
    selectedTag() {
      return this.$store.getters[GETTER.DASHBOARD.SELECTED_TAG];
    },
  },
  watch: {
    items() {
      if (this.swiper) this.swiper?.destroy();
      this.initSwiper();
    },
    selectedTag({ name }) {
      this.handleItemClick(this.items.findIndex(({ name: itemName }) => itemName === name));
    },
  },
  mounted() {
    this.initSwiper();
  },
  methods: {
    initSwiper() {
      this.swiper = new Swiper(this.$refs.swiper, {
        slidesPerView: 'auto',
        freeMode: true,
        slideToClickedSlide: false,
        on: {
          slideChange: (swiper) => {
            this.handleItemClick(swiper.realIndex);
          },
        },
      });
    },
    handleItemClick(index) {
      if (this.swiper.activeIndex !== index) this.swiper.slideTo(index);
      if (this.selectedTag.name !== this.items[index].name) {
        this.$store.commit(MUTATION.DASHBOARD.SET_SELECTED_TAG, this.items[index]);
      }
    },
    handleTagItemClick(index) {
      if (this.isTouchDevice) return;
      const columnElement = this.$refs.swiper.children[0].children[index];
      if (columnElement) {
        const listRect = this.$refs.swiper.children[0].getBoundingClientRect();
        const columnRect = columnElement.getBoundingClientRect();
        const scrollLeft = columnRect.left - listRect.left;
        this.$refs.swiper.scrollTo({
          left: scrollLeft,
          behavior: 'smooth',
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/mixins.scss';
@import 'swiper/scss';

.columns-list {
  margin-left: -$sp-m + -$sp-xs;
  padding-left: $sp-m + $sp-xs;
  width: auto;
  display: flex;
  white-space: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: -ms-autohiding-scrollbar;

  > .swiper-wrapper {
    display: flex;
  }

  &__item {
    width: auto;
    margin-bottom: 0;

    @include for('mobile-only') {
      margin-right: $sp-s;
    }

    &--empty {
      height: 100vh;
      min-width: 368px;

      @media (max-width: 375px) {
        min-width: calc(100vw - $sp-l - $sp-s);
      }

      @include for('tablet') {
        height: unset;
        min-width: unset;
      }
    }
  }

  &__tag {
    display: none;

    @include for('tablet') {
      display: flex;
    }
  }

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
